import { MetadataEntitiesClient, MetadataEntityCreateRequest, MetadataEntityUpdateRequest } from "@api/apiClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "@api/axiosInstance";
import { BENCHMARK_MODEL_TYPES_QUERY_KEY } from "@api/benchmarkModelTypes";
import { BENCHMARK_CATALOGS_KEY } from "@api/benchmarkCatalog";

const client = new MetadataEntitiesClient(undefined, axios);

const BENCHMARK_MODEL_TYPE = "BenchmarkModelType";
const MODEL_TYPE_QUERY_KEY_BENCHMARK = ["modelTypes", BENCHMARK_MODEL_TYPE] as const;

/** UI/route key for Benchmark Model Type in Meta Data Management. */
export const BENCHMARK_MODEL_TYPE_UI_KEY = "benchmark-model-type" as const;

/** Backend metadata entity type for Benchmark Model Type. */
export const BENCHMARK_MODEL_TYPE_BACKEND = BENCHMARK_MODEL_TYPE;

function invalidateAllBenchmarkQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: MODEL_TYPE_QUERY_KEY_BENCHMARK });
  queryClient.invalidateQueries({ queryKey: BENCHMARK_MODEL_TYPES_QUERY_KEY });
  queryClient.invalidateQueries({ queryKey: BENCHMARK_CATALOGS_KEY });
  queryClient.invalidateQueries({ queryKey: ["benchmarkCatalog"] });
}

/**
 * Benchmark Model Type only: list for settings grid.
 * Sole owner of get/add/update/delete for this type.
 */
export const useBenchmarkModelTypeData = (options?: any) => {
  return useQuery({
    ...options,
    queryKey: MODEL_TYPE_QUERY_KEY_BENCHMARK,
    initialData: [],
    queryFn: async () => client.getMetaDataByType(BENCHMARK_MODEL_TYPE),
  });
};

/** Benchmark Model Type only: add. */
export const useAddBenchmarkModelType = (options?: any) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: ({ value }: { value: string }) => {
      const createRequest = new MetadataEntityCreateRequest({
        name: value,
        type: BENCHMARK_MODEL_TYPE,
      });
      return client.createMetaDataEntity(createRequest);
    },
    onSuccess: (...args: unknown[]) => {
      invalidateAllBenchmarkQueries(queryClient);
      options?.onSuccess?.(...args);
    },
  });
};

/** Benchmark Model Type only: delete by id. */
export const useDeleteBenchmarkModelType = (options?: any) => {
  const queryClient = useQueryClient();
  const { onSuccess: callerOnSuccess, ...rest } = options ?? {};
  return useMutation<boolean, unknown, { id: string }>({
    ...rest,
    mutationFn: ({ id }) => client.deleteMetaDataEntitybyId(id),
    onSuccess: (...args: unknown[]) => {
      invalidateAllBenchmarkQueries(queryClient);
      callerOnSuccess?.(...args);
    },
  });
};

/** Benchmark Model Type only: update (e.g. rename). */
export const useUpdateBenchmarkModelType = (options?: any) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: (payload: MetadataEntityUpdateRequest) => client.updateMetaDataEntity(payload),
    onSuccess: (...args: unknown[]) => {
      invalidateAllBenchmarkQueries(queryClient);
      options?.onSuccess?.(...args);
    },
  });
};
